import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useThemeColors } from '../hooks/useThemeColors';
import { usePrimateAuth } from '../hooks/usePrimateAuth';
import { trackSignOut } from '../lib/analytics';
import { ACCOUNT_ITEMS, ADMIN_GROUPS, ADMIN_ITEM_COUNT, type NavItem } from '../primate/nav/navConfig';

export interface MobileMorePanelProps {
  open: boolean;
  onClose: () => void;
  isSignedIn: boolean;
  userInitials?: string;
  userName?: string;
  userEmail?: string;
  isPro?: boolean;
  planTier?: string;
  /** Admin gate. When omitted, falls back to usePrimateAuth().isAdmin. */
  isAdmin?: boolean;
  demoVideos?: Array<{ id: string; display_name: string; thumbnail_url?: string }>;
  userVideos?: Array<{ id: string; display_name: string; thumbnail_url?: string }>;
  streamSessionCount?: number;
  onLiveStreamsSelect?: () => void;
  onVideoLibrarySelect?: () => void;
  onUserVideoSelect?: (id: string) => void;
  onDemoVideoSelect?: (id: string) => void;
  onSignOut?: () => void;
}

function ChevronIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
      <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function BackIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 16 16" fill="none">
      <path d="M10 4l-4 4 4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SectionHeader({ label, trailing }: { label: string; trailing?: string }) {
  const c = useThemeColors();
  return (
    <div style={{
      padding: '16px 20px 6px',
      fontSize: 12, // 12px typography floor on mobile (PRI-535)
      fontWeight: 700,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      color: c.textMuted,
      userSelect: 'none',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    }}>
      <span>{label}</span>
      {trailing && <span style={{ opacity: 0.8 }}>{trailing}</span>}
    </div>
  );
}

/** A nav row with optional leading icon, description, badge and chevron. */
function Row({
  label, desc, icon, badge, accent, onPress, testid,
}: {
  label: string;
  desc?: string;
  icon?: React.ReactNode;
  badge?: number;
  accent?: string;
  onPress: () => void;
  testid?: string;
}) {
  const c = useThemeColors();
  return (
    <button
      data-testid={testid}
      onClick={onPress}
      style={{
        display: 'flex', alignItems: 'center', gap: 13,
        width: '100%', padding: '13px 20px',
        background: 'none', border: 'none', cursor: 'pointer',
        color: c.textPrimary,
        fontFamily: 'inherit',
        WebkitTapHighlightColor: 'transparent',
        textAlign: 'left',
        borderBottom: `1px solid ${c.border}`,
        minHeight: 52,
      }}
    >
      {icon && (
        <span style={{
          width: 20, height: 20, flex: '0 0 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: accent ?? c.textMuted,
        }}>{icon}</span>
      )}
      <span style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <span style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.2 }}>{label}</span>
        {desc && <span style={{ fontSize: 12, color: c.textMuted, marginTop: 1 }}>{desc}</span>}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {badge !== undefined && badge > 0 && (
          <span style={{
            background: c.signal, color: '#fff', fontSize: 11, fontWeight: 700,
            borderRadius: 10, padding: '1px 7px', minWidth: 20, textAlign: 'center',
          }}>{badge}</span>
        )}
        <span style={{ color: c.textMuted, opacity: 0.7 }}><ChevronIcon /></span>
      </span>
    </button>
  );
}

const ADMIN_ACCENT = '#7A4FE0';

export default function MobileMorePanel({
  open,
  onClose,
  isSignedIn,
  userInitials,
  userName,
  userEmail,
  isPro,
  planTier,
  isAdmin: isAdminProp,
  demoVideos = [],
  userVideos: _userVideos = [],
  streamSessionCount: _streamSessionCount = 0,
  onLiveStreamsSelect: _onLiveStreamsSelect,
  onVideoLibrarySelect: _onVideoLibrarySelect,
  onUserVideoSelect: _onUserVideoSelect,
  onDemoVideoSelect,
  onSignOut,
}: MobileMorePanelProps) {
  const c = useThemeColors();
  const navigate = useNavigate();
  const auth = usePrimateAuth();
  const isAdmin = isAdminProp ?? auth.isAdmin;

  // Sub-sheet view state. Reset to root whenever the sheet closes so re-opening
  // always lands on the main menu (no stale Admin view).
  const [view, setView] = useState<'root' | 'admin'>('root');
  useEffect(() => { if (!open) setView('root'); }, [open]);

  function handleNav(path: string) {
    onClose();
    navigate(path);
  }
  function handleDemoVideo(id: string) { onClose(); onDemoVideoSelect?.(id); }
  function handleSignOut() {
    if (!window.confirm('Sign out of Primate Vision?')) return;
    trackSignOut();
    onClose();
    onSignOut?.();
  }

  const planLabel = (planTier && planTier !== 'free')
    ? planTier.charAt(0).toUpperCase() + planTier.slice(1)
    : (isPro ? 'Pro' : 'Free');

  return (
    <div
      data-testid="more-panel"
      data-open={open ? 'true' : 'false'}
      data-view={view}
      style={{
        position: 'fixed',
        inset: 0,
        // Above the onboarding OnboardingHighlights overlay (z-index 9999) as
        // defense-in-depth: the demo is unmounted when this menu opens, but the
        // menu must always outrank any transient onboarding layer. (2026-06-11)
        zIndex: 10010,
        pointerEvents: open ? 'auto' : 'none',
      }}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute', inset: 0,
          background: 'rgba(0,0,0,0.45)',
          opacity: open ? 1 : 0,
          transition: 'opacity 300ms ease',
        }}
      />

      <SwipeDownSheet onClose={onClose} open={open} bgSurface={c.bgSurface}>
        {/* Drag handle */}
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 12, paddingBottom: 4, flexShrink: 0 }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: c.textMuted, opacity: 0.35 }} />
        </div>

        {view === 'root' ? (
          <RootView
            c={c}
            isSignedIn={isSignedIn}
            isAdmin={isAdmin}
            userName={userName}
            userEmail={userEmail}
            userInitials={userInitials}
            planLabel={planLabel}
            demoVideos={demoVideos}
            onDemoVideo={handleDemoVideo}
            onNav={handleNav}
            onOpenAdmin={() => setView('admin')}
            onSignOut={handleSignOut}
            onSignIn={() => handleNav('/login')}
            onSignUp={() => handleNav('/signup')}
          />
        ) : (
          <AdminView c={c} onBack={() => setView('root')} onNav={handleNav} />
        )}
      </SwipeDownSheet>
    </div>
  );
}

// ── Root view ───────────────────────────────────────────────────────────────
function RootView({
  c, isSignedIn, isAdmin, userName, userEmail, userInitials, planLabel,
  demoVideos,
  onDemoVideo, onNav, onOpenAdmin,
  onSignOut, onSignIn, onSignUp,
}: {
  c: ReturnType<typeof useThemeColors>;
  isSignedIn: boolean;
  isAdmin: boolean;
  userName?: string;
  userEmail?: string;
  userInitials?: string;
  planLabel: string;
  demoVideos: Array<{ id: string; display_name: string }>;
  onDemoVideo: (id: string) => void;
  onNav: (p: string) => void;
  onOpenAdmin: () => void;
  onSignOut: () => void;
  onSignIn: () => void;
  onSignUp: () => void;
}) {
  return (
    <>
      {/* Identity header */}
      {isSignedIn && userName && (
        <div style={{ padding: '4px 20px 6px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 34, height: 34, borderRadius: '50%', background: c.signal,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 13, fontWeight: 700, flexShrink: 0,
          }}>
            {userInitials ?? userName.slice(0, 2).toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: c.textPrimary, lineHeight: 1.2 }}>{userName}</div>
            {userEmail && <div style={{ fontSize: 12, color: c.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userEmail}</div>}
          </div>
        </div>
      )}
      {isSignedIn && (
        <div style={{ display: 'flex', gap: 8, padding: '4px 20px 6px' }}>
          {isAdmin && <PlanPill label="Admin" accent={ADMIN_ACCENT} />}
          <PlanPill label={planLabel} accent={c.signal} />
        </div>
      )}

      {/* Demo Videos — session history is now the canonical sessions surface (PRI-494 P3) */}
      {demoVideos.length > 0 && (
        <>
          <SectionHeader label="Demo Videos" />
          {demoVideos.map(v => <Row key={v.id} label={v.display_name} onPress={() => onDemoVideo(v.id)} />)}
        </>
      )}

      {/* Account */}
      <SectionHeader label="Account" />
      {isSignedIn ? (
        <>
          {ACCOUNT_ITEMS.map((it: NavItem) => (
            <Row key={it.href} label={it.label} icon={it.icon} onPress={() => onNav(it.href)} testid={`more-account-${it.label.toLowerCase()}`} />
          ))}

          {/* Admin Tools — single entry that opens the grouped sub-sheet */}
          {isAdmin && (
            <>
              <SectionHeader label="Admin" trailing={`${ADMIN_ITEM_COUNT} tools`} />
              <Row
                label="Admin Tools"
                desc="Waitlist, CRM, DevOps, Prompts…"
                icon={<IconToolbox />}
                accent={ADMIN_ACCENT}
                onPress={onOpenAdmin}
                testid="more-admin-tools"
              />
            </>
          )}

          <div style={{ padding: '14px 20px 4px' }}>
            <button
              onClick={onSignOut}
              style={{
                width: '100%', padding: '12px 0', background: 'none',
                border: `1px solid ${c.border}`, borderRadius: 12, cursor: 'pointer',
                color: c.textMuted, fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
                WebkitTapHighlightColor: 'transparent',
              }}
            >Sign Out</button>
          </div>
        </>
      ) : (
        <>
          <Row label="Sign In" onPress={onSignIn} />
          <Row label="Sign Up" onPress={onSignUp} />
        </>
      )}
    </>
  );
}

// ── Admin sub-sheet view ─────────────────────────────────────────────────────
function AdminView({
  c, onBack, onNav,
}: {
  c: ReturnType<typeof useThemeColors>;
  onBack: () => void;
  onNav: (p: string) => void;
}) {
  return (
    <div data-testid="more-admin-view">
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: '4px 14px 10px',
        borderBottom: `1px solid ${c.border}`,
      }}>
        <button data-testid="more-admin-back" onClick={onBack} style={{
          border: 'none', background: 'none', color: c.textMuted, cursor: 'pointer',
          padding: 4, display: 'flex', alignItems: 'center',
        }}><BackIcon /></button>
        <span style={{ fontSize: 15, fontWeight: 700, color: c.textPrimary }}>Admin Tools</span>
      </div>
      {ADMIN_GROUPS.map(group => (
        <div key={group.title}>
          <SectionHeader label={group.title} />
          {group.items.map(it => (
            <Row
              key={it.href}
              label={it.label}
              desc={it.desc}
              icon={it.icon}
              accent={ADMIN_ACCENT}
              onPress={() => onNav(it.href)}
              testid={`more-admin-${it.href.split('/').pop()}`}
            />
          ))}
        </div>
      ))}
      <div style={{ height: 16 }} />
    </div>
  );
}

function PlanPill({ label, accent }: { label: string; accent: string }) {
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, color: accent,
      background: `${accent}1A`, border: `1px solid ${accent}40`,
      borderRadius: 8, padding: '3px 9px',
    }}>{label}</span>
  );
}

// ── Inline icons used by the root view ───────────────────────────────────────
function IconToolbox() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M3 7h18v13H3z"/><path d="M8 7V4h8v3"/><line x1="3" y1="12" x2="21" y2="12"/><rect x="10" y="10" width="4" height="4" rx="1"/></svg>;
}

// ── Swipe-down-to-dismiss sheet wrapper ─────────────────────────────────────
function SwipeDownSheet({
  open, onClose, bgSurface, children,
}: {
  open: boolean;
  onClose: () => void;
  bgSurface: string;
  children: React.ReactNode;
}) {
  const touchStartY = useRef(0);
  return (
    <div
      onTouchStart={(e) => { touchStartY.current = e.touches[0].clientY; }}
      onTouchEnd={(e) => {
        if (e.changedTouches[0].clientY - touchStartY.current > 80) onClose();
      }}
      style={{
        position: 'absolute',
        left: 0, right: 0, bottom: 0,
        maxHeight: '85dvh',
        background: bgSurface,
        borderRadius: '16px 16px 0 0',
        display: 'flex',
        flexDirection: 'column',
        transform: open ? 'translateY(0)' : 'translateY(100%)',
        transition: 'transform 300ms ease',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        paddingBottom: 'calc(49px + env(safe-area-inset-bottom, 0px))',
      }}
    >
      {children}
    </div>
  );
}
